import React from 'react';
import { Modal } from '@stormwater-software/lib-ui';
import { Link } from 'react-router-dom';

/**
 * One upcoming inspection, exactly as `GET /divisions/:id/calendar/upcoming` returns it.
 *
 * `kind` is the discriminator the server reads off `is_scheduled_inspection`:
 * `assigned` is a form attached under Project Settings > Inspection Settings carrying
 * its `next_inspection_date`, `one_time` is an inspection somebody scheduled by hand.
 * Only the second kind can carry notes, which is why `notes` is nullable rather than
 * absent — the assigned branch selects `NULL::text` to keep the UNION aligned.
 */
export type UpcomingInspection = {
  /**
   * The junction row id for a stored inspection, and `projected-<templateId>-<day>` for
   * a projected one — which is why this is not just a number. A projection has no row of
   * its own to be identified by.
   */
  id: number | string;
  projectId: number;
  projectName: string;
  clientName: string | null;
  day: string;
  kind: 'assigned' | 'one_time';
  formName: string | null;
  formTypeId: number | null;
  formType: string | null;
  inspectorId: number | null;
  /**
   * Null in two different situations that read the same here: nobody was ever assigned,
   * and the assignee's account has since been deleted. The second is not rare — 385 of
   * the 7,741 assigned rows name a deleted user — and both mean the same thing to
   * whoever is looking at the calendar, which is that no current account is going to
   * turn up. Hence one label rather than two.
   */
  inspectorName: string | null;
  /**
   * The form's cadence — "Weekly", "Bi-Weekly", "Annual". Null for a one-time
   * inspection, which has no cadence by definition, so the field is omitted for those
   * rather than shown empty.
   */
  frequency: string | null;
  notes: string | null;
};

interface Props {
  show: boolean;
  onClose: () => void;
  dateLabel: string;
  upcoming: UpcomingInspection[];
  divisionId: number | string;
  /**
   * Whole-modal rather than per-row: every row here shares the cell's day, so they are
   * all late or none are. Decided by the cell, which is the only place that knows what
   * "today" is.
   */
  isPastDue?: boolean;
}

/**
 * SW2-2343 — everything due on a day, for the whole selection.
 *
 * Deliberately the same shape as `CalendarDayModal`: same shell class, so it inherits
 * the 80vh cap and the scrolling body. That cap is load-bearing rather than cosmetic
 * here — a single day in CMS - Colorado holds 156 upcoming inspections across 156
 * projects, where the submitted side of the same month rarely passes a dozen. Without
 * the cap this list would run several screens off the bottom with the close control
 * somewhere above it.
 */
const UpcomingDayModal: React.FC<Props> = ({
  show,
  onClose,
  dateLabel,
  upcoming,
  divisionId,
  isPastDue = false,
}) => {
  if (!show) return null;

  return (
    <Modal
      show={show}
      title={<span className='calendar-day-modal__title'>{dateLabel}</span>}
      handleClose={onClose}
      hideButtons
      dismissOnBgClick
      classes='calendar-day-modal-shell'
    >
      <div className='calendar-day-modal upcoming-day-modal'>
        <p
          className={`calendar-day-modal__count${
            isPastDue ? ' upcoming-day-modal__count--past-due' : ''
          }`}
        >
          {upcoming.length} {upcoming.length === 1 ? 'inspection' : 'inspections'}{' '}
          {isPastDue ? 'past due' : 'upcoming'}
        </p>

        <ul className='calendar-day-modal__list'>
          {upcoming.map(row => (
            // Unique across stored and projected rows alike — the junction row id for
            // the first, `projected-<templateId>-<day>` for the second — so it stands
            // alone as a key with no index needed.
            <li className='calendar-day-modal__item' key={row.id}>
              <div className='calendar-day-modal__project'>
                {row.projectName}
                {row.kind === 'one_time' && (
                  // Worth calling out: an assigned form recurs and will reappear, a
                  // one-time inspection was put on this day on purpose and will not
                  // come back if it is missed.
                  <span className='upcoming-day-modal__badge'>One-time</span>
                )}
              </div>
              {row.clientName && (
                <div className='calendar-day-modal__client'>{row.clientName}</div>
              )}
              <dl className='calendar-day-modal__fields'>
                <div>
                  <dt>Form</dt>
                  <dd>{row.formName || '—'}</dd>
                </div>
                <div>
                  <dt>Type</dt>
                  <dd>{row.formType || '—'}</dd>
                </div>
                <div>
                  <dt>Inspector</dt>
                  <dd>{row.inspectorName || 'Unassigned'}</dd>
                </div>
                {row.frequency && (
                  <div>
                    <dt>Frequency</dt>
                    <dd>{row.frequency}</dd>
                  </div>
                )}
              </dl>
              {row.notes && (
                <p className='upcoming-day-modal__notes'>{row.notes}</p>
              )}
              <Link
                className='calendar-day-modal__link'
                to={`/divisions/${divisionId}/projects/${row.projectId}/inspections`}
                onClick={onClose}
              >
                View project
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
};

export default UpcomingDayModal;
